/**
 * The session the browser holds (piece 14). 🔨 Wherever the application is
 * served, the session rides the HttpOnly cookie the sign-in set, and the
 * browser's storage holds only a marker that one is held (decision 0310); a
 * script never sees the token. In development the web and the API stand on
 * different origins, so the token itself is kept and sent as the bearer.
 * Who the session names is read from the API each time, never cached beside it.
 */
export const SESSION_STORAGE_KEY = 'bms2.session';

/** What storage holds where the cookie carries the session: a session is held, and that is all a script knows. */
export const SESSION_HELD_MARKER = 'cookie';

const bearerTransport = (): boolean => __ENV_KIND__ === 'development';

export function readSessionToken(): string | null {
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return null;
  }
}

export function writeSessionToken(token: string): void {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, bearerTransport() ? token : SESSION_HELD_MARKER);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more
    // Storage refused: the session lasts the page and no longer.
  }
}

export function clearSessionToken(): void {
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more
    // Nothing to clear.
  }
}

/** The server's own read of the session a request carries, which answers `signedIn` whether or not one is held (piece 12). */
export const SESSION_READ_PATH = '/auth/session';

/**
 * 🔒 The marker follows the server's answer, never the other way round
 * (decision 3540; T2313). A browser can lose its marker while its HttpOnly
 * cookie still names a live session: Safari clears a site's storage on its own,
 * and the Login Screen used to drop it on every open. This asks the server's own
 * session read and writes the marker back where a session is held, and drops it
 * where none is. The answer is true or false, or null where the server could not
 * be read, which leaves the marker as it stood.
 *
 * 🔨 A marker written while the read was in flight is never dropped by it: a
 * sign-in that finished in that time holds a session the read did not see.
 * 🔨 The caller hands in its own request, the frame's `apiFetch` with no
 * redirect on a 401, because the request module imports this one.
 */
export async function syncSessionMarker(apiBaseUrl: string, read: (url: string) => Promise<Response>): Promise<boolean | null> {
  const before = readSessionToken();
  const response = await read(`${apiBaseUrl}${SESSION_READ_PATH}`).catch(/* silent: a server that cannot be reached answers nothing about the session, and the marker stands as it was. */ () => null);
  if (!response) return null;
  // A refused token is no session: the read answers 401 to a bearer the server no longer honours.
  const body = response.ok ? ((await response.json().catch(/* silent: a body that is not JSON reads as no answer, and the marker stands. */ () => null)) as { signedIn?: unknown } | null) : response.status === 401 ? { signedIn: false } : null;
  if (!body) return null;
  const held = body.signedIn === true;
  if (readSessionToken() !== before) return held;
  if (held && before === null) markSessionHeld();
  if (!held && before !== null) clearSessionToken();
  return held;
}

/** Writes the marker alone, whatever the transport: a session the cookie carries, restored from the server's answer. */
export function markSessionHeld(): void {
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, SESSION_HELD_MARKER);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
  }
}

/** The Authorization header for a request in development, and nothing where the cookie carries the session or none is held. */
export function sessionHeaders(): Record<string, string> {
  const token = readSessionToken();
  return token && token !== SESSION_HELD_MARKER ? { authorization: `Bearer ${token}` } : {};
}

/** The trusted device's proof (decision 0324): held in storage only under the development bearer transport; the HttpOnly cookie carries it otherwise. */
export const DEVICE_STORAGE_KEY = 'bms2.device';

export function readDeviceToken(): string | null {
  if (!bearerTransport()) return null;
  try {
    return window.localStorage.getItem(DEVICE_STORAGE_KEY);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return null;
  }
}

export function writeDeviceToken(token: string): void {
  if (!bearerTransport()) return;
  try {
    window.localStorage.setItem(DEVICE_STORAGE_KEY, token);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more
    // Storage refused: the device is trusted for this page and no longer.
  }
}
